import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaTelegramPlane,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Load from "../components/Load";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import { fetchMostViewedPosts } from "../redux/slices/postSlice";

const FaIcon = ({ icon: Icon, size = 15 }: any) => (
  <Icon size={size} />
);

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/i, "")
  .replace(/\/+$/, "");

const getUploadUrl = (
  file?: string | null
) => {
  if (
    !file ||
    !String(file).trim()
  ) {
    return "";
  }

  const value =
    String(file).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  if (
    value.startsWith("/uploads/")
  ) {
    return encodeURI(
      `${API_URL}${value}`
    );
  }

  if (
    value.startsWith("uploads/")
  ) {
    return encodeURI(
      `${API_URL}/${value}`
    );
  }

  if (
    value.startsWith("/")
  ) {
    return encodeURI(
      `${API_URL}${value}`
    );
  }

  return encodeURI(
    `${API_URL}/uploads/${value}`
  );
};

const getYouTubeVideoId = (
  url?: string | null
) => {
  if (!url) return "";

  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname
        .replace(/^\//, "")
        .split("/")[0];
    }

    if (parsed.hostname.includes("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) {
        return (
          parsed.pathname
            .split("/embed/")[1]
            ?.split("/")[0] || ""
        );
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        return (
          parsed.pathname
            .split("/shorts/")[1]
            ?.split("/")[0] || ""
        );
      }

      return parsed.searchParams.get("v") || "";
    }
  } catch {
    return "";
  }

  return "";
};

const getYouTubeEmbedUrl = (
  url?: string | null
) => {
  const videoId = getYouTubeVideoId(url);

  return videoId
    ? `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`
    : "";
};

const getUserId = (): number | null => {
  try {
    const userData = JSON.parse(
      localStorage.getItem("user") || "{}"
    );

    const userId = Number(
      userData?.user_id
    );

    return Number.isInteger(userId) &&
      userId > 0
      ? userId
      : null;
  } catch {
    return null;
  }
};

const formatDateTime = (
  date?: string | null
) => {
  if (!date) return "";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Defaultvideo: React.FC = () => {
  const dispatch = useDispatch<any>();
  const shareRef = useRef<HTMLDivElement | null>(null);

  const {
    mostViewedPosts = [],
    mostViewedLoading = false,
  } = useSelector((state: any) => state.posts);

  const [shareOpenPostId, setShareOpenPostId] =
    useState<number | string | null>(null);

  const [copiedPostId, setCopiedPostId] =
    useState<number | string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const userId =
      getUserId();

    dispatch(
      fetchMostViewedPosts(
        userId
      ) as any
    );
  }, [dispatch]);

  useEffect(() => {
    const handleOutsideClick = (
      event: MouseEvent
    ) => {
      if (
        shareRef.current &&
        !shareRef.current.contains(
          event.target as Node
        )
      ) {
        setShareOpenPostId(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  const videoPosts = useMemo(
    () =>
      (mostViewedPosts || []).filter(
        (post: any) => {
          const youtubeUrl =
            String(
              post?.youtube_url || ""
            ).trim();

          const postVideo =
            String(
              post?.post_video || ""
            ).trim();

          return Boolean(
            youtubeUrl ||
            postVideo
          );
        }
      ),
    [mostViewedPosts]
  );

  const filteredVideoPosts = useMemo(() => {
    const query = searchTerm
      .trim()
      .toLowerCase();

    if (!query) return videoPosts;

    return videoPosts.filter((post: any) =>
      [
        post.title,
        post.short_description,
        post.client_name,
        post.category_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [searchTerm, videoPosts]);

  const openShare = (url: string) => {
    window.open(
      url,
      "_blank",
      "width=700,height=500"
    );
  };

  const copyLink = async (
    postId: number | string,
    shareUrl: string
  ) => {
    try {
      await navigator.clipboard.writeText(
        shareUrl
      );
    } catch {
      const input =
        document.createElement("input");

      input.value = shareUrl;

      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }

    setCopiedPostId(postId);

    setTimeout(() => {
      setCopiedPostId(null);
    }, 1400);
  };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <main className="main-content right-chat-active bs-video-page">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="bs-video-feed-wrap">
              <section className="bs-video-header-panel">
                <div className="bs-video-heading-row">
                  <div>
                    <h2>Trending Videos</h2>

                    <p>
                      Most viewed video updates shared by clubs.
                    </p>
                  </div>

                  <span className="bs-video-count-pill">
                    {filteredVideoPosts.length}
                    {filteredVideoPosts.length === 1
                      ? " Video"
                      : " Videos"}
                  </span>
                </div>

                <div className="bs-video-search">
                  <i className="feather-search" />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) =>
                      setSearchTerm(
                        event.target.value
                      )
                    }
                    placeholder="Search videos, clubs, or topics..."
                    aria-label="Search videos"
                  />

                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() =>
                        setSearchTerm("")
                      }
                      aria-label="Clear search"
                    >
                      <i className="feather-x" />
                    </button>
                  )}
                </div>
              </section>

              {mostViewedLoading ? (
                <div className="bs-video-state-card">
                  <div className="bs-video-loader" />

                  <h4>Loading videos</h4>

                  <p>
                    Please wait while we fetch the
                    latest club videos.
                  </p>
                </div>
              ) : filteredVideoPosts.length > 0 ? (
                <div className="bs-video-grid">
                  {filteredVideoPosts.map(
                    (
                      post: any,
                      index: number
                    ) => {
                      const postId =
                        post.post_id ||
                        post.id ||
                        index;

                      const pageTitle =
                        post.title ||
                        post.short_description ||
                        "Bharat Sangam Video";

                      const shareUrl = `${API_URL}/share/news-details/${postId}`;
                      const encodedShareUrl =
                        encodeURIComponent(
                          shareUrl
                        );
                      const encodedTitle =
                        encodeURIComponent(
                          pageTitle
                        );

                      const isShareOpen =
                        shareOpenPostId === postId;

                      const isCopied =
                        copiedPostId === postId;

                      return (
                        <article
                          key={postId}
                          className="bs-video-card"
                        >
                          <div className="bs-video-header">
                            <img
                              src={
                                post.logo_image
                                  ? getUploadUrl(
                                      post.logo_image
                                    )
                                  : "/assets/images/user.png"
                              }
                              alt={
                                post.client_name ||
                                "Club"
                              }
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/assets/images/user.png";
                              }}
                              className="bs-video-logo"
                            />

                            <div className="bs-video-source">
                              <h4>
                                {post.client_name ||
                                  "Club"}
                              </h4>

                              <span>
                                {formatDateTime(
                                  post.created_at
                                )}
                              </span>
                            </div>

                            <span className="bs-video-type">
                              Video
                            </span>
                          </div>

                          <div className="bs-player-box">
                            {post.youtube_url &&
                            getYouTubeEmbedUrl(
                              post.youtube_url
                            ) ? (
                              <iframe
                                src={getYouTubeEmbedUrl(
                                  post.youtube_url
                                )}
                                title={pageTitle}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                loading="lazy"
                              />
                            ) : post.post_video ? (
                              <video
                                src={getUploadUrl(
                                  post.post_video
                                )}
                                controls
                                playsInline
                                preload="metadata"
                                width="100%"
                                height="100%"
                              >
                                Your browser does not
                                support the video tag.
                              </video>
                            ) : (
                              <div className="bs-video-unavailable">
                                <i className="feather-video-off" />
                                <span>
                                  Video unavailable
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="bs-video-content">
                            <h3>{pageTitle}</h3>

                            {post.short_description &&
                              post.short_description !==
                                post.title && (
                                <p>
                                  {
                                    post.short_description
                                  }
                                </p>
                              )}

                            <a
                              href={`/news-details/${postId}`}
                            >
                              View full post
                              <i className="feather-arrow-right" />
                            </a>
                          </div>

                          <div className="bs-video-actions">
                            <div className="bs-action-stat">
                              <i className="feather-eye" />
                              <span>
                                {post.view_count || 0}
                              </span>
                            </div>

                            <div className="bs-action-stat">
                              <i className="feather-thumbs-up" />
                              <span>
                                {post.like_count || 0}
                              </span>
                            </div>

                            <div className="bs-action-stat">
                              <i className="feather-message-circle" />
                              <span>
                                {post.comment_count ||
                                  0}
                              </span>
                            </div>

                            <div
                              className="bs-share-action"
                              ref={
                                isShareOpen
                                  ? shareRef
                                  : null
                              }
                            >
                              <button
                                type="button"
                                className={`bs-share-btn ${
                                  isShareOpen
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() =>
                                  setShareOpenPostId(
                                    isShareOpen
                                      ? null
                                      : postId
                                  )
                                }
                              >
                                <i className="feather-share-2" />
                                <span>Share</span>
                              </button>

                              {isShareOpen && (
                                <div className="bs-share-popup">
                                  <div className="bs-share-title-row">
                                    <strong>
                                      Share video
                                    </strong>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShareOpenPostId(
                                          null
                                        )
                                      }
                                    >
                                      ×
                                    </button>
                                  </div>

                                  <div className="bs-share-icons">
                                    <button
                                      type="button"
                                      className="facebook"
                                      title="Facebook"
                                      onClick={() =>
                                        openShare(
                                          `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`
                                        )
                                      }
                                    >
                                      <FaIcon
                                        icon={
                                          FaFacebookF
                                        }
                                      />
                                    </button>

                                    <button
                                      type="button"
                                      className="twitter"
                                      title="Twitter"
                                      onClick={() =>
                                        openShare(
                                          `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedTitle}`
                                        )
                                      }
                                    >
                                      <FaIcon
                                        icon={FaTwitter}
                                      />
                                    </button>

                                    <button
                                      type="button"
                                      className="whatsapp"
                                      title="WhatsApp"
                                      onClick={() =>
                                        openShare(
                                          `https://wa.me/?text=${encodeURIComponent(
                                            `${pageTitle} ${shareUrl}`
                                          )}`
                                        )
                                      }
                                    >
                                      <FaIcon
                                        icon={FaWhatsapp}
                                      />
                                    </button>

                                    <button
                                      type="button"
                                      className="linkedin"
                                      title="LinkedIn"
                                      onClick={() =>
                                        openShare(
                                          `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`
                                        )
                                      }
                                    >
                                      <FaIcon
                                        icon={
                                          FaLinkedinIn
                                        }
                                      />
                                    </button>

                                    <button
                                      type="button"
                                      className="telegram"
                                      title="Telegram"
                                      onClick={() =>
                                        openShare(
                                          `https://t.me/share/url?url=${encodedShareUrl}&text=${encodedTitle}`
                                        )
                                      }
                                    >
                                      <FaIcon
                                        icon={
                                          FaTelegramPlane
                                        }
                                      />
                                    </button>
                                  </div>

                                  <div className="bs-copy-box">
                                    <span>
                                      {shareUrl}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyLink(
                                          postId,
                                          shareUrl
                                        )
                                      }
                                    >
                                      {isCopied
                                        ? "Copied"
                                        : "Copy"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className="bs-video-empty">
                  <div className="bs-video-empty-icon">
                    <i className="feather-video-off" />
                  </div>

                  <h3>
                    {searchTerm
                      ? "No matching videos"
                      : "No videos available yet"}
                  </h3>

                  <p>
                    {searchTerm
                      ? "Try a different keyword or clear the search."
                      : "Most viewed club videos will appear here."}
                  </p>

                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() =>
                        setSearchTerm("")
                      }
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}

              {!mostViewedLoading &&
                filteredVideoPosts.length > 0 && (
                  <Load />
                )}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .bs-video-page {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          font-family: 'Inter', sans-serif;
          background:
            linear-gradient(
              180deg,
              #f8f9fc 0%,
              #f1f3f8 100%
            );
        }

        .bs-video-page .middle-sidebar-bottom,
        .bs-video-page .middle-sidebar-left {
          width: 100%;
          max-width: 100%;
        }

        .bs-video-feed-wrap {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding: 0 0 40px;
        }

        .bs-video-header-panel {
          width: 100%;
          margin: 0 0 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f8fbff 58%,
              #f1f6ff 100%
            );
          box-shadow:
            0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .bs-video-heading-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .bs-video-heading-row h2 {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .bs-video-heading-row p {
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
        }

        .bs-video-count-pill {
          display: inline-flex;
          align-items: center;
          min-height: 26px;
          padding: 0 9px;
          flex-shrink: 0;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
        }

        .bs-video-search {
          position: relative;
          display: flex;
          align-items: center;
          width: 420px;
          max-width: 100%;
          margin-bottom: 0;
        }

        .bs-video-search > i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 1;
          color: #9aa4b2;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .bs-video-search input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 34px 0 36px;
          outline: none;
          color: #1f2937;
          background: rgba(255, 255, 255, 0.92);
          font-size: 13px;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.05);
          transition:
            box-shadow 0.2s ease,
            border-color 0.2s ease;
        }

        .bs-video-search input::placeholder {
          color: #a5b0c0;
        }

        .bs-video-search input:focus {
          border-color: #2F6FE4;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.04),
            0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .bs-video-search button {
          position: absolute;
          right: 10px;
          top: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          color: #64748b;
          background: #eef1f6;
          font-size: 10px;
          transform: translateY(-50%);
          cursor: pointer;
        }

        .bs-video-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .bs-video-card {
          min-width: 0;
          overflow: visible;
          border: 1px solid #e5ebf4;
          border-radius: 18px;
          background: #ffffff;
          box-shadow:
            0 10px 28px rgba(15, 23, 42, 0.045);
          transition:
            transform 0.25s ease,
            border-color 0.25s ease,
            box-shadow 0.25s ease;
        }

        .bs-video-card:hover {
          transform: translateY(-3px);
          border-color: #bae6fd;
          box-shadow:
            0 16px 36px rgba(15, 23, 42, 0.09);
        }

        .bs-video-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 13px 14px 11px;
        }

        .bs-video-logo {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border: 2px solid #ffffff;
          border-radius: 14px;
          object-fit: cover;
          background: #eff6ff;
          box-shadow:
            0 5px 12px rgba(15, 23, 42, 0.08);
        }

        .bs-video-source {
          min-width: 0;
          flex: 1;
        }

        .bs-video-source h4 {
          margin: 0;
          overflow: hidden;
          color: #0f172a;
          font-size: 13px;
          font-weight: 800;
          line-height: 1.3;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-video-source span {
          display: block;
          margin-top: 4px;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 600;
        }

        .bs-video-type {
          padding: 6px 10px;
          border-radius: 999px;
          color: #0369a1;
          background: #e0f2fe;
          font-size: 10px;
          font-weight: 800;
        }

        .bs-player-box {
          width: calc(100% - 24px);
          aspect-ratio: 16 / 9;
          margin: 0 12px;
          overflow: hidden;
          border-radius: 14px;
          background: #020617;
        }

        .bs-player-box iframe,
        .bs-player-box video {
          display: block;
          width: 100%;
          height: 100%;
          border: 0;
          object-fit: contain;
          background: #000000;
        }

        .bs-video-unavailable {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 9px;
          color: #cbd5e1;
          font-size: 13px;
          font-weight: 700;
        }

        .bs-video-unavailable i {
          font-size: 28px;
        }

        .bs-video-content {
          padding: 13px 14px 10px;
        }

        .bs-video-content h3 {
          display: -webkit-box;
          min-height: 42px;
          margin: 0;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          color: #0f172a;
          font-size: 15px;
          font-weight: 800;
          line-height: 1.4;
        }

        .bs-video-content p {
          display: -webkit-box;
          min-height: 38px;
          margin: 7px 0 0;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          color: #64748b;
          font-size: 12px;
          line-height: 1.55;
        }

        .bs-video-content a {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin-top: 10px;
          color: #0284c7;
          font-size: 12px;
          font-weight: 800;
          text-decoration: none;
        }

        .bs-video-actions {
          display: grid;
          grid-template-columns:
            auto auto auto 1fr;
          align-items: center;
          gap: 14px;
          margin: 0 16px;
          padding: 12px 0 15px;
          border-top: 1px solid #edf2f7;
        }

        .bs-action-stat {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
        }

        .bs-action-stat i {
          font-size: 16px;
        }

        .bs-share-action {
          position: relative;
          justify-self: end;
        }

        .bs-share-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          border-radius: 10px;
          padding: 7px 10px;
          color: #475569;
          background: #f1f5f9;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .bs-share-btn.active {
          color: #0369a1;
          background: #e0f2fe;
        }

        .bs-share-popup {
          position: absolute;
          right: 0;
          bottom: 42px;
          z-index: 9999;
          width: 292px;
          padding: 14px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #ffffff;
          box-shadow:
            0 18px 45px rgba(15, 23, 42, 0.16);
        }

        .bs-share-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .bs-share-title-row strong {
          color: #0f172a;
          font-size: 13px;
        }

        .bs-share-title-row button {
          width: 28px;
          height: 28px;
          border: 0;
          border-radius: 9px;
          color: #475569;
          background: #f1f5f9;
          font-size: 18px;
          cursor: pointer;
        }

        .bs-share-icons {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .bs-share-icons button {
          width: 39px;
          height: 39px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 0;
          border-radius: 12px;
          color: #ffffff;
          cursor: pointer;
        }

        .bs-share-icons .facebook {
          background: #1877f2;
        }

        .bs-share-icons .twitter {
          background: #1da1f2;
        }

        .bs-share-icons .whatsapp {
          background: #25d366;
        }

        .bs-share-icons .linkedin {
          background: #0077b5;
        }

        .bs-share-icons .telegram {
          background: #229ed9;
        }

        .bs-copy-box {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px;
          border-radius: 11px;
          color: #ffffff;
          background: #0f172a;
        }

        .bs-copy-box span {
          min-width: 0;
          flex: 1;
          overflow: hidden;
          color: #cbd5e1;
          font-size: 11px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-copy-box button {
          border: 0;
          padding: 0;
          color: #7dd3fc;
          background: transparent;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .bs-video-state-card,
        .bs-video-empty {
          width: 100%;
          min-height: 200px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #ffffff;
          box-shadow: none;
        }

        .bs-video-empty-icon {
          width: 44px;
          height: 44px;
          margin-bottom: 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          box-shadow: none;
        }

        .bs-video-empty-icon i {
          font-size: 20px;
        }

        .bs-video-empty h3,
        .bs-video-state-card h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .bs-video-empty p,
        .bs-video-state-card p {
          max-width: 330px;
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        .bs-video-empty button {
          margin-top: 12px;
          border: 0;
          border-radius: 999px;
          padding: 7px 12px;
          color: #2f6fe4;
          background: #eef4ff;
          font-size: 10.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .bs-video-loader {
          width: 30px;
          height: 30px;
          margin-bottom: 12px;
          border: 3px solid #dbeafe;
          border-top-color: #2f6fe4;
          border-radius: 50%;
          animation: bs-spin 0.8s linear infinite;
        }

        @keyframes bs-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 1400px) {
          .bs-video-page {
            padding-right: 290px;
          }
        }

        @media (max-width: 1200px) {
          .bs-video-page {
            padding-left: 20px;
            padding-right: 20px;
          }

          .bs-video-feed-wrap {
            margin-left: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 820px) {
          .bs-video-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .bs-video-page {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .bs-video-page .middle-sidebar-bottom,
          .bs-video-page .middle-sidebar-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .bs-video-feed-wrap {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding: 0 0 84px;
          }

          .bs-video-header-panel {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .bs-video-heading-row h2 {
            font-size: 21px;
          }

          .bs-video-search {
            width: 100%;
          }

          .bs-video-card {
            border-radius: 18px;
          }

          .bs-video-actions {
            grid-template-columns:
              auto auto auto 1fr;
            gap: 9px;
          }

          .bs-action-stat {
            font-size: 11px;
          }

          .bs-share-popup {
            right: -8px;
            width: min(292px, calc(100vw - 34px));
          }

          .bs-video-state-card,
          .bs-video-empty {
            min-height: 190px;
            border-radius: 15px;
          }
        }
      `}</style>

      <Popupchat />
      <Appfooter />
    </Fragment>
  );
};

export default Defaultvideo;
